<div
  [formGroup]="parentForm()"
  class="radio-group"
  [ngClass]="{
    'border rounded-large white-bg surface-primary': showBorders(),
    'ps-medium-y': (!translationPrefix() || !titleText()) && !isChild() && !isResponsive() && showBorders(),
    'pr-small-y': (!translationPrefix() || !titleText()) && !isChild() && isResponsive() && showBorders(),
    'new-brand': isNewBrand,
  }"
  role="radiogroup"
  [attr.aria-labelledby]="hasTitleLabel() ? 'radio-group-label-' + controlName() : null"
  [attr.aria-label]="!hasTitleLabel() ? ariaLabel() : null"
>
  @if (hasTitleLabel()) {
    <div
      [id]="'radio-group-label-' + controlName()"
      class="font-body-1 font-body-medium"
      data-testid="radio-item-group-title"
      [ngClass]="{
        'mr-small-b': isResponsive(),
        'mr-small-t': isResponsive() && showBorders(),
        'mr-small-x': isResponsive() && showBorders(),
        'ms-medium-t': !isResponsive() && showBorders(),
        'ms-medium-x': !isResponsive() && showBorders(),
        'ms-small-b': !isResponsive(),
      }"
    >
      <span
        class="text-icon-secondary"
        [ngClass]="{
          'nordic-blue-400-text': !control().invalid || !control().touched,
          'error-900-text': control().invalid && control().touched,
        }"
      >
        {{ translationPrefix() + '.label' | finLocalization }}
        @if (isRequired()) {
          <span> *</span>
        }
      </span>
    </div>
  }
  <div
    class="flex"
    [class]="theme()"
    [ngClass]="{
      'flex--column': theme() === RadioButtonTheme.ROUND && !isHorizontal(),
      'flex--column--mobile': isHorizontal() && isResponsive(),
      'flex--row': theme() === RadioButtonTheme.ROUND && isHorizontal(),
      'flex--wrap': isHorizontal() && isResponsive(),
      'has-divider': showDivider(),
    }"
  >
    @for (option of options(); track option.value; let first = $first, last = $last) {
      @let childContent = getChildContent(option.value);
      @switch (theme()) {
        @case (RadioButtonTheme.ROUND) {
          <label
            [attr.aria-checked]="control().value === option.value"
            [attr.data-testid]="testIdPrefix() ? testIdPrefix() + '-' + option.value : option.value"
            [class]="'flex ps-xxsmall-y radio-item ' + theme()"
            [formGroup]="parentForm()"
            [ngClass]="{
              checked: control().value === option.value,
              disabled: control().disabled || option.disabled,
              error: control().invalid && control().touched,
              'ps-medium-x': !isHorizontal() && !isChild() && !isResponsive() && showBorders(),
              'pr-small-x': !isHorizontal() && !isChild() && isResponsive() && showBorders(),
              'ms-xxsmall-t': !first && !isHorizontal(),
              'center-radio': !first && !last,
              'last-radio': last,
              'first-radio': first,
            }"
          >
            <div
              class="flex relative regular-type radio-wrapper"
              [class.grey-600-text]="control().disabled || option.disabled"
              [class.text-icon-disabled-text]="control().disabled || option.disabled"
            >
              <input
                class="is-vishidden"
                role="radio"
                [class.sensitive]="isSensitive()"
                [id]="id + '-' + option.value"
                type="radio"
                [attr.name]="name() || controlName()"
                [value]="option.value"
                [formControlName]="controlName()"
                [name]="controlName()"
                [attr.disabled]="option.disabled ? 'disabled' : null"
              />
              <span aria-hidden="true">
                @if (control().value === option.value) {
                  <fcom-icon
                    [class]="'radio-icon ' + iconSize()"
                    [dataTestId]="'icon-' + SvgLibraryIcon.RADIO_SELECTED"
                    [name]="SvgLibraryIcon.RADIO_SELECTED"
                  />
                } @else {
                  <fcom-icon
                    [class]="'radio-icon ' + iconSize()"
                    [class.error-900-text]="control().invalid && control().touched"
                    [class.signal-error-notification-border-icon-text]="control().invalid && control().touched"
                    [dataTestId]="'icon-' + SvgLibraryIcon.RADIO_UNSELECTED"
                    [name]="SvgLibraryIcon.RADIO_UNSELECTED"
                  />
                }
              </span>
              <div class="full-width ms-xsmall-l">
                <div class="label font-body-1 medium-type font-label-medium">
                  {{
                    option.labelText
                      ? option.labelText
                      : ((option.label ? option.label : translationPrefix() + '.' + option.value) | finLocalization)
                  }}
                </div>
                @if (option.descriptionHTML || option.descriptionText) {
                  <div class="description-label font-body-2 nordic-blue-400-text text-icon-secondary font-body-small">
                    @if (option.descriptionHTML) {
                      <div
                        [innerHTML]="
                          option.descriptionHTML
                            | finLocalization
                            | finAddClasses
                              : {
                                  a: 'font-body-2 font-body-small nordic-blue-900-text text-icon-primary medium-type',
                                  p: 'font-body-2 font-body-small nordic-blue-400-text text-icon-primary',
                                }
                            | finSafe: 'html'
                        "
                      ></div>
                    } @else {
                      {{ option.descriptionText | finLocalization }}
                    }
                  </div>
                }
              </div>
            </div>
          </label>
        }
        @case (RadioButtonTheme.BOX) {
          <label
            [attr.data-testid]="testIdPrefix() ? testIdPrefix() + '-' + option.value : option.value"
            [class]="'radio-item ' + theme()"
            [ngClass]="{
              'first-radio': first,
              'last-radio': last,
              'center-radio': !first && !last,
              'two-item-mode': twoItemMode(),
              checked: control().value === option.value,
              error: control().invalid && control().touched,
              disabled: control().disabled || option.disabled,
            }"
            class="flex relative flex--center"
            [formGroup]="parentForm()"
            [attr.aria-checked]="control().value === option.value"
          >
            <div class="radio-wrapper relative">
              <div class="flex flex--center regular-type ps-xxlarge-x">
                <input
                  class="is-vishidden"
                  role="radio"
                  [id]="id"
                  type="radio"
                  [class.sensitive]="isSensitive()"
                  [attr.name]="name() || controlName()"
                  [value]="option.value"
                  [formControlName]="controlName()"
                  [name]="controlName()"
                />
                <span class="flex flex--middle label font-body-1 medium-type nowrap relative font-label-medium">
                  <fcom-icon
                    class="icon icon-small"
                    [class.active]="control().value === option.value"
                    [name]="SvgLibraryIcon.CHECKMARK"
                  />
                  {{
                    option.labelText
                      ? option.labelText
                      : ((option.label ? option.label : translationPrefix() + '.' + option.value) | finLocalization)
                  }}
                </span>
              </div>
            </div>
          </label>
        }
      }
      @if (!isHorizontal() && control().value === option.value && childContent) {
        <div
          class="nordic-blue-900-text text-icon-primary ps-xxsmall-y ms-xsmall-l"
          [attr.data-testid]="'radio-item-group-child-content-' + option.value"
          [ngClass]="{
            'ms-large-l': !showBorders(),
            'ps-xxsmall-l': !showBorders(),
            'ms-xxlarge-l': showBorders(),
          }"
        >
          <ng-container [ngTemplateOutlet]="childContent" />
        </div>
      }
      @if (showDivider() && !last) {
        <div
          aria-hidden="true"
          class="border-bottom"
          [ngClass]="{
            'ms-xxsmall-t': !isResponsive(),
            'mr-xxsmall-y': isResponsive(),
          }"
          data-testid="radio-item-group-divider"
        ></div>
      }
    }
  </div>

  @if (showRequiredError() && translationPrefix() && errorText() && control().invalid && control().touched) {
    <div
      aria-live="polite"
      class="error-label font-body-2 font-body-small"
      [ngClass]="{
        'ms-medium-y': !isResponsive(),
        'mr-small-y': isResponsive(),
        'ms-medium-x': showBorders() && !isResponsive(),
        'mr-small-x': showBorders() && isResponsive(),
      }"
    >
      @if (control().errors?.required) {
        <span class="error-900-text signal-error-notification-border-icon-text">
          {{ translationPrefix() + '.required' | finLocalization }}
        </span>
      }
    </div>
  }

  @if (translationPrefix() && helperText() && !(control().invalid && control().touched)) {
    <div
      class="helper-label font-body-2 font-body-small"
      [ngClass]="{
        'ms-medium-y': !isResponsive(),
        'mr-small-y': isResponsive(),
        'ms-medium-x': showBorders() && !isResponsive(),
        'mr-small-x': showBorders() && isResponsive(),
      }"
    >
      <span class="nordic-blue-400-text text-icon-secondary">
        {{ translationPrefix() + '.helper' | finLocalization }}
      </span>
    </div>
  }
</div>
